import {useEffect, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {initialValues} from './_module'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useAppDispatch} from '../../../../redux/hooks'
import {useTranslation} from 'react-i18next'
import CustomSelect from 'app/custom/CustomSelect'
import {
  getCampusUnitFaculties,
  getCampusUnits,
  onlineApplicationStore,
} from '../../../../redux/dua/online-application/onlineApplicationSlice'
import {showMessage} from '_metronic/helpers/swalHelper'

export function OnlineApplicationForm() {
  const {t} = useTranslation()
  const [loading, setLoading] = useState(false)
  const dispatch = useAppDispatch()
  const [faculties, setFaculties] = useState<any>([])
  const [campusUnits, setCampusUnits] = useState<any>([])

  const {i18n} = useTranslation()
  const currentLang = i18n.language

  const handleChangeLanguage = (lang: string) => {
    i18n.changeLanguage(lang)
    window.location.reload()
  }

  const languages = [
    {
      lang: 'dr',
      nameDr: 'دری',
      nameEn: 'Dari',
      namePa: 'دری',
      flag: toAbsoluteUrl('/media/flags/afghanistan.svg'),
    },
    {
      lang: 'pa',
      nameDr: 'پشتو',
      nameEn: 'Pashto',
      namePa: 'پشتو',
      flag: toAbsoluteUrl('/media/flags/afghanistan.svg'),
    },
    {
      lang: 'en',
      nameDr: 'انگلیسی',
      nameEn: 'English',
      namePa: 'انگلیسی',
      flag: toAbsoluteUrl('/media/flags/united-states.svg'),
    },
  ]

  useEffect(() => {
    dispatch(getCampusUnits(currentLang)).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') setCampusUnits(res.payload)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onlineApplicationSchema = Yup.object().shape({
    full_name: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('LABEL.FULL_NAME')})),
    father_name: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('LABEL.FATHER_NAME')})),
    whatsapp_number: Yup.string()
      .required(t('VALIDATION.REQUIRED_', {name: t('LABEL.WHATSAPP_NUMBER')}))
      .matches(/^[0-9]+$/, t('VALIDATION.MUST_BE_NUMBER', {name: t('LABEL.WHATSAPP_NUMBER')}))
      .min(10, t('VALIDATION.MIN_CHARACTER_', {name: t('LABEL.WHATSAPP_NUMBER'), quantity: 10}))
      .max(10, t('VALIDATION.MAX_CHARACTER_', {name: t('LABEL.WHATSAPP_NUMBER'), quantity: 10})),
    email: Yup.string().email(t('VALIDATION.INVALID_EMAIL_FORMAT')).nullable(),
    campus_unit_id: Yup.string().required(
      t('VALIDATION.REQUIRED_', {name: t('LABEL.CAMPUS_UNIT')})
    ),
    comments: Yup.string().max(
      500,
      t('VALIDATION.MAX_CHARACTER_', {name: t('LABEL.COMMENTS'), quantity: 500})
    ),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: onlineApplicationSchema,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setLoading(true)
      const formData = new FormData()
      formData.append('full_name', values.full_name)
      formData.append('father_name', values.father_name)
      formData.append('whatsapp_number', values.whatsapp_number)
      formData.append('email', values.email)
      formData.append('campus_unit_id', values.campus_unit_id)
      formData.append('faculty_id', values.faculty_id)
      formData.append('comments', values.comments)

      dispatch(onlineApplicationStore(formData)).then((res) => {
        if (res.meta.requestStatus === 'fulfilled') {
          showMessage(t('MESSAGES.ONLINE_APPLICATION_SAVE_MESSAGE'), 'success')
          resetForm()
        } else if (res.meta.requestStatus === 'rejected') {
          showMessage(t('MESSAGES.SERVER_ERROR'), 'error')
        }
        setLoading(false)
        setSubmitting(false)
      })
    },
  })

  const renderError = (field: string) =>
    formik.touched[field] &&
    formik.errors[field] && (
      <div className='fv-plugins-message-container'>
        <span role='alert' className='text-danger fw-semibold'>
          {formik.errors[field] as string}
        </span>
      </div>
    )

  const handleGetFaculties = (campus_unit_id: any) => {
    const param = {
      lang: currentLang,
      campus_unit_id,
    }

    if (campus_unit_id) {
      formik.setFieldValue('campus_unit_id', campus_unit_id)

      dispatch(getCampusUnitFaculties(param)).then((res: any) => {
        setFaculties(res.payload)
      })
    } else {
      setFaculties([])
      formik.setFieldValue('campus_unit_id', null)
    }
  }

  return (
    <div className='container-lg'>
      {/* 🔹 Top Header Card */}
      <div className='card shadow-sm mb-5 px-10 py-5'>
        <div className='d-flex flex-wrap justify-content-between align-items-center gap-3 gap-md-0'>
          {/* Left: Logo + Title */}
          <div className='d-flex align-items-center flex-wrap text-center text-md-start'>
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/logo/logo-dua.png')}
              className='h-60px me-3 mb-2 mb-md-0'
            />
            <h3 className='fw-bold text-dark mb-0 fs-4'>{t('LABEL.APPLICATION_FORM')}</h3>
          </div>

          {/* Right: Language Buttons */}
          <div
            className='d-flex flex-wrap flex-md-nowrap gap-2'
            style={{
              overflowX: 'auto', // allow horizontal scroll on very small screens
              paddingBottom: '0.25rem', // small spacing for scrollbar if needed
            }}
          >
            {languages.map((l) => {
              const displayName =
                currentLang === 'dr' ? l.nameDr : currentLang === 'pa' ? l.namePa : l.nameEn

              const isActive = currentLang === l.lang

              return (
                <button
                  key={l.lang}
                  onClick={() => handleChangeLanguage(l.lang)}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = '#f2f2f2'
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = '#ffffff'
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.60rem 1.3rem',
                    borderRadius: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: isActive ? 700 : 600,
                    color: isActive ? '#fff' : '#333',
                    backgroundColor: isActive ? '#2870B6' : '#ffffff',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease, color 0.2s ease',
                    whiteSpace: 'nowrap', // keep button text in one line
                    flex: '0 0 auto', // prevent buttons from stretching
                  }}
                >
                  <span style={{display: 'flex', alignItems: 'center'}}>
                    <img
                      src={l.flag}
                      alt={displayName}
                      style={{
                        borderRadius: 0,
                        width: '24px',
                        height: '16px',
                        objectFit: 'cover',
                        marginRight: '0.5rem',
                      }}
                    />
                  </span>
                  {displayName}
                </button>
              )
            })}
          </div>
        </div>

        <hr className='my-4' />

        {/* Welcome Text Section */}
        <div>
          <h5 className='fw-bold text-primary mb-3'>
            Welcome to the official <strong>DUA University</strong> online application platform.
          </h5>
          <p className='mb-2'>
            Please complete this form carefully using your official information, exactly as it
            appears on your passport.
          </p>
          <p className='mb-2'>
            Ensure that all data entered is accurate and complete. Once the form has been submitted,
            our Admissions Office or one of our authorised representatives will review your
            application.
          </p>
          <p className='mb-2'>
            You will then be contacted for document verification and the next steps in the admission
            process. Only complete applications will be considered.
          </p>
          <p className='mb-2'>
            <strong>Important:</strong> Submitting this application does not create any financial
            obligation.
          </p>
          <p className='mb-0'>
            Admission fees will only be requested after official confirmation of your acceptance.
          </p>
        </div>
      </div>

      {/* 🔹 Form Card (your existing form stays unchanged) */}
      <div className='card'>
        <div className='card-body'>
          <form className='form' onSubmit={formik.handleSubmit} noValidate autoComplete='off'>
            {/* Row 1: Full Name / Father Name */}
            <div className='row mb-6'>
              <div className='col-md-6'>
                <label className='required'>{t('LABEL.FULL_NAME')}</label>
                <input
                  {...formik.getFieldProps('full_name')}
                  className={clsx('form-control', {
                    'is-invalid': formik.touched.full_name && formik.errors.full_name,
                  })}
                  type='text'
                  placeholder={t('VALIDATION.REQUIRED_', {name: t('LABEL.FULL_NAME')})}
                />
                {renderError('full_name')}
              </div>

              <div className='col-md-6'>
                <label className='required'>{t('LABEL.FATHER_NAME')}</label>
                <input
                  {...formik.getFieldProps('father_name')}
                  className={clsx('form-control', {
                    'is-invalid': formik.touched.father_name && formik.errors.father_name,
                  })}
                  type='text'
                  placeholder={t('VALIDATION.REQUIRED_', {name: t('LABEL.FATHER_NAME')})}
                />
                {renderError('father_name')}
              </div>
            </div>

            {/* Row 2: WhatsApp / Email */}
            <div className='row mb-6'>
              <div className='col-md-6'>
                <label className='required'>{t('LABEL.WHATSAPP_NUMBER')}</label>
                <input
                  {...formik.getFieldProps('whatsapp_number')}
                  className={clsx('form-control', {
                    'is-invalid': formik.touched.whatsapp_number && formik.errors.whatsapp_number,
                  })}
                  type='text'
                  placeholder={t('VALIDATION.REQUIRED_', {name: t('LABEL.WHATSAPP_NUMBER')})}
                />
                {renderError('whatsapp_number')}
              </div>

              <div className='col-md-6'>
                <label>{t('PROFILE.EMAIL')}</label>
                <input
                  {...formik.getFieldProps('email')}
                  className={clsx('form-control', {
                    'is-invalid': formik.touched.email && formik.errors.email,
                  })}
                  type='email'
                  placeholder={t('VALIDATION.REQUIRED_', {name: t('PROFILE.EMAIL')})}
                />
                {renderError('email')}
              </div>
            </div>

            {/* Row 3: Department / Campus */}
            <div className='row mb-6'>
              <div className='col-md-6'>
                <label className='required'>{t('LABEL.CAMPUS_UNIT')}</label>
                <CustomSelect
                  name='campus_unit_id'
                  className='input'
                  onChange={(cc: any) => handleGetFaculties(cc.id)}
                  value={formik.values.campus_unit_id}
                  options={campusUnits}
                  placeholderText={t('LABEL.CAMPUS_UNIT')}
                />
                {renderError('campus_unit_id')}
              </div>

              <div className='col-md-6'>
                <label>{t('LABEL.FACULTY')}</label>
                <CustomSelect
                  name='faculty_id'
                  className='input'
                  onChange={(fa: any) => formik.setFieldValue('faculty_id', fa.id)}
                  value={formik.values.faculty_id}
                  options={faculties}
                  placeholderText={t('LABEL.FACULTY')}
                />
                {renderError('faculty_id')}
              </div>
            </div>

            {/* Row 4: Comments */}
            <div className='mb-6'>
              <label>{t('LABEL.COMMENTS')}</label>
              <textarea
                {...formik.getFieldProps('comments')}
                className={clsx('form-control', {
                  'is-invalid': formik.touched.comments && formik.errors.comments,
                })}
                rows={4}
                placeholder={t('VALIDATION.WRITE_ADDITIONAL_COMMENTS')}
              />
              {renderError('comments')}
            </div>

            {/* ✅ Button with loader (Metronic style) */}
            <div className='shadow-sm m-1 p-5 mt-10'>
              <p className='text-danger fw-bold'>
                I hereby confirm that all the information provided in this application is true,
                complete, and accurate to the best of my knowledge. I understand that any false or
                misleading information may result in the withdrawal of my application or admission.
              </p>
              <button
                type='submit'
                className='btn btn-primary btn-sm fw-bold'
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {!loading && (
                  <>
                    <i className='fas fa-save mx-1'></i>
                    {t('BUTTONS.SAVE')}
                  </>
                )}

                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    {t('GLOBAL.LOADING')}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
